import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';

import { useSettledFlagState } from './useLaunchDarklyFlagState';

/**
 * Hook to evaluate a LaunchDarkly feature flag (Web)
 * Uses React Web SDK's built-in useFlags hook
 *
 * @template T The type of the flag value (defaults to boolean)
 * @param flagKey - The feature flag key from LaunchDarkly
 * @param defaultValue - Default value if flag is not found or unavailable
 * @param options.enabled - When `false`, returns `defaultValue` without evaluating
 *   the flag. Reading a key off the `useFlags()` result is what registers a
 *   LaunchDarkly exposure, so this is the only way to keep ineligible users out
 *   of an experiment running on the flag.
 * @returns The flag value with type T
 *
 * @example
 * // Boolean flag
 * const isFeatureEnabled = useLaunchDarklyFlag('new-feature', false);
 *
 * @example
 * // JSON flag
 * const config = useLaunchDarklyFlag<{ color: string }>('feature-config', { color: 'blue' });
 *
 * @example
 * // Gated flag — no exposure is recorded while `isEligible` is false
 * const variant = useLaunchDarklyFlag('bnpl-experiment', 'control', {
 *   enabled: isEligible,
 * });
 *
 * @see docs/FEATURE_FLAGS_LD.md - Complete LaunchDarkly integration guide
 */
/**
 * Local development override:
 * In __DEV__ builds, set `localStorage['__ldOverride.<flagKey>'] = '<jsonValue>'`
 * to force a flag value without changing the LD project. JSON-encoded so it
 * works for booleans, strings, numbers, and JSON flags. Remove the entry to
 * fall back to LD-evaluated values. Stripped from production builds.
 */
export const readDevOverride = <T>(camelCaseKey: string): T | undefined => {
  if (!__DEV__ || typeof localStorage === 'undefined') return undefined;
  const raw = localStorage.getItem(`__ldOverride.${camelCaseKey}`);
  if (raw === null) return undefined;
  try {
    // Unsafe cast: dev-only escape hatch. Caller is responsible for matching
    // the localStorage value to the flag's expected type. Stripped from
    // production builds via the __DEV__ guard above.
    return JSON.parse(raw) as T;
  } catch {
    return undefined;
  }
};

// The web SDK exposes flags camelCased, so every lookup goes through this.
export const toCamelCaseKey = (flagKey: string): string =>
  flagKey.replace(/-([a-z])/g, (g) => g[1].toUpperCase());

export const useLaunchDarklyFlag = <T = boolean>(
  flagKey: string,
  defaultValue: T,
  options?: { enabled?: boolean },
): T => {
  // Called unconditionally to satisfy the rules of hooks; the exposure is
  // registered by the property read below, not by this call.
  const flags = useFlags();

  if (options?.enabled === false) return defaultValue;

  const camelCaseKey = toCamelCaseKey(flagKey);

  const override = readDevOverride<T>(camelCaseKey);
  if (override !== undefined) return override;

  const result = (flags[camelCaseKey] as T) ?? defaultValue;
  return result;
};

/**
 * Whether LaunchDarkly has answered with real flag data yet (Web)
 *
 * `useLaunchDarklyFlag` returns the caller's default until the SDK payload
 * arrives, so a surface whose gate-off state is terminal must wait for this
 * before treating a `false` as the shop's real answer.
 *
 * @param flagKey - The feature flag key from LaunchDarkly
 * @returns `true` once the flag value can be trusted
 */
export const useIsLaunchDarklyFlagResolved = (flagKey: string): boolean => {
  // Enumerating keys registers no exposure; only reading a value does.
  const flags = useFlags();
  const ldClient = useLDClient();
  const camelCaseKey = toCamelCaseKey(flagKey);

  // No provider above means there is no client to wait for, so the caller's
  // default already IS the resolved answer. A delivered flag set proves the
  // payload arrived; an empty one is not proof of the opposite, which is why
  // the wait below is bounded.
  const isResolved =
    !ldClient ||
    readDevOverride(camelCaseKey) !== undefined ||
    camelCaseKey in flags ||
    Object.keys(flags).length > 0;

  return useSettledFlagState(isResolved);
};
